import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useLang } from '../../contexts/langContext';

interface ThumbnailUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => Promise<void>;
  totalSlides: number;
}

function ThumbnailUploadDialog({ open, onClose, onUpload, totalSlides }: ThumbnailUploadDialogProps) {
  const { lang } = useLang();
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    // Sort files by name
    const sortedFiles = [...acceptedFiles].sort((a, b) => 
      a.name.localeCompare(b.name, undefined, { numeric: true })
    );

    // Validate file types
    const invalidFiles = sortedFiles.filter(file => 
      !['image/jpeg', 'image/jpg'].includes(file.type)
    );

    if (invalidFiles.length > 0) {
      setError(
        lang === 'en'
          ? `Invalid file type(s). Only JPG/JPEG files are allowed. Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`
          : `文件类型无效。仅支持 JPG/JPEG 文件。无效文件：${invalidFiles.map(f => f.name).join(', ')}`
      );
      return;
    }

    setStagedFiles(prev => {
      const newFiles = [...prev, ...sortedFiles];
      if (newFiles.length > totalSlides) {
        setError(
          lang === 'en'
            ? `Too many files selected. Expected ${totalSlides} files.`
            : `选择的文件过多。需要 ${totalSlides} 个文件。`
        );
        return prev;
      }
      setError(null);
      return newFiles;
    });
  }, [totalSlides, lang]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
    },
  });

  const handleRemoveFile = useCallback((index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
    setError(null);
  }, []);

  const handleUpload = useCallback(async () => {
    if (stagedFiles.length !== totalSlides) {
      setError(
        lang === 'en'
          ? `Number of files (${stagedFiles.length}) does not match the number of slides (${totalSlides})`
          : `文件数量（${stagedFiles.length}）与幻灯片数量（${totalSlides}）不匹配`
      );
      return;
    }

    setIsUploading(true);
    setCurrentFileIndex(0);
    setUploadProgress(0);

    try {
      await onUpload(stagedFiles);
      onClose();
    } catch (err) {
      setError(lang === 'en' ? 'Failed to upload thumbnails. Please try again.' : '缩略图上传失败，请重试。');
    } finally {
      setIsUploading(false);
      setCurrentFileIndex(0);
      setUploadProgress(0);
    }
  }, [stagedFiles, totalSlides, onUpload, onClose, lang]);

  const handleClose = useCallback(() => {
    if (!isUploading) {
      setStagedFiles([]);
      setError(null);
      setUploadProgress(0);
      setCurrentFileIndex(0);
      onClose();
    }
  }, [isUploading, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '50vh' }
      }}
    >
      <DialogTitle>{lang === 'en' ? 'Upload Thumbnails' : '上传缩略图'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {lang === 'en'
              ? `Upload thumbnails for all slides (${totalSlides} slides)`
              : `上传所有幻灯片的缩略图（${totalSlides} 张幻灯片）`}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
        
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            bgcolor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: 'pointer',
            mb: 2,
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
          <Typography>
            {isDragActive
              ? (lang === 'en' ? 'Drop the files here' : '将文件拖放到此处')
              : (lang === 'en' ? 'Drag & drop files here, or click to select files' : '拖放文件到此处，或点击选择文件')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lang === 'en' ? 'Only JPG/JPEG files are allowed' : '仅支持 JPG/JPEG 文件'}
          </Typography>
        </Box>

        {stagedFiles.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {lang === 'en'
                ? `Staged Files (${stagedFiles.length} / ${totalSlides})`
                : `已选文件（${stagedFiles.length} / ${totalSlides}）`}
            </Typography>
            <List dense>
              {stagedFiles.map((file, index) => (
                <ListItem 
                  key={index} 
                  divider={index !== stagedFiles.length - 1}
                  sx={{
                    bgcolor: index === currentFileIndex && isUploading ? 'action.selected' : 'inherit',
                  }}
                >
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      disabled={isUploading}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {lang === 'en'
                ? `Uploading ${currentFileIndex + 1} of ${stagedFiles.length}`
                : `正在上传第 ${currentFileIndex + 1} 个，共 ${stagedFiles.length} 个`}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(uploadProgress / stagedFiles.length) * 100} 
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          {lang === 'en' ? 'Cancel' : '取消'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={stagedFiles.length !== totalSlides || isUploading}
        >
          {lang === 'en' ? 'Upload' : '上传'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ThumbnailUploadDialog; 